import { useState, useCallback, useEffect } from 'react';
import type { MinimapData } from '@/components/editor/WorkflowCanvas';
import type { WorkflowBlock, Connector, BlockGroup, BlockType, PortSide } from '@/types/workflow';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '@/i18n/LanguageContext';
import type { ConnectionDebugInfo } from '@/hooks/useDocuments';
import { MobilePortraitLayout } from './MobilePortraitLayout';

export interface MobileLayoutProps {
  // Workflow data
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  selectedBlockId: string | null;
  selectedBlock: WorkflowBlock | null;
  selectedGroupId: string | null;
  selectedGroup: BlockGroup | null;
  workflowName: string;

  // Layer navigation
  visibleBlocks: WorkflowBlock[];
  visibleConnectors: Connector[];
  collapsedGroups: BlockGroup[];
  expandedGroups: BlockGroup[];
  breadcrumbs: { id: string | null; name: string }[];
  layerPath: string[];
  currentLayerGroupId: string | null;

  // Modes
  connectMode: boolean;
  groupMode: boolean;
  groupSelection: string[];

  // Callbacks
  onSelectBlock: (id: string) => void;
  onSelectGroup: (id: string | null) => void;
  onCanvasClick: () => void;
  onCreateBlock: (type: BlockType, x: number, y: number) => WorkflowBlock;
  onUpdateBlockPosition: (id: string, x: number, y: number) => void;
  onUpdateBlockIcon: (id: string, iconKey: string) => void;
  onConnect: (fromId: string, toId: string, fromPort?: PortSide, toPort?: PortSide) => void;
  onToggleGroupSelection: (id: string) => void;
  onToggleConnectMode: () => void;
  onToggleGroupMode: () => void;
  onUpdateGroupPosition: (id: string, x: number, y: number) => void;
  onConvertToLayer: (blockId: string) => void;
  onUpdateConnectorWaypoints: (id: string, waypoints: { x: number; y: number }[]) => void;
  onUpdateConnectorLabel: (id: string, label: string) => void;
  onNavigateInto: (groupId: string) => void;
  onNavigateToLevel: (level: number) => void;
  onNavigateToLayer: (groupId: string | null) => void;
  onSetSelectedBlockId: (id: string | null) => void;

  // Block/group editing
  onUpdateBlock: (id: string, updates: Partial<WorkflowBlock>) => void;
  onDeleteBlock: () => void;
  onDeleteConnector?: (id: string) => void;
  onUpdateGroup: (id: string, updates: Partial<BlockGroup>) => void;
  onDeleteGroup: () => void;
  onUpdateWorkflowName: (name: string) => void;

  // Top bar actions
  onSave: () => void;
  onLoad: () => void;
  onExportPNG: () => void;
  onSimulate: () => void;
  onClear: () => void;
  onImportJson: () => void;
  onCopyJson: () => void;
  onHelp: () => void;
  onAiJson: () => void;
  onAiAssistant: () => void;
  onPreview: () => void;
  onLogin?: () => void;
  onCloudWorkspace?: () => void;
  onShareWorkspace?: () => void;
  onFeatureRequest?: () => void;
  onBugReport?: () => void;
  onDonate?: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // Status
  isConnected: boolean;
  connectionDebugInfo?: ConnectionDebugInfo;
  isDirty: boolean;
  announcement: string;
  canvasInnerRef: React.RefObject<HTMLDivElement>;
  /** Mobile gesture-handlers (sheet-switch, undo, sidebars, etc.) */
  mobileGestures?: import('@/components/editor/WorkflowCanvas').MobileGestureHandlers;
}

/** Shared props passed from MobileLayout to portrait/landscape sub-layouts */
export interface MobileSubLayoutProps {
  props: MobileLayoutProps;
  minimapData: MinimapData | null;
  handleMinimapDataChange: (data: MinimapData) => void;
  handleRegisterZoomHandlers: (handlers: { zoomIn: () => void; zoomOut: () => void; resetZoom: () => void; fitToScreen: () => void }) => void;
  handleMobileAddBlock: (type: BlockType) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleFitView: () => void;
}

export function MobileLayout(props: MobileLayoutProps) {
  const { t } = useTranslation();

  // Try to lock orientation to landscape for more canvas space
  useEffect(() => {
    try {
      // ScreenOrientation.lock saknas i lib.dom.d.ts men finns på de
      // flesta moderna browsers (Chrome/Edge/Samsung Internet) — cast.
      const so = screen.orientation as ScreenOrientation & {
        lock?: (orientation: 'landscape' | 'portrait') => Promise<void>;
      };
      if (so?.lock) {
        so.lock('landscape').catch(() => {
          // Silently fail — lock only works in fullscreen/PWA mode
        });
      }
    } catch {
      // Screen Orientation API not available
    }
    return () => {
      try { screen.orientation?.unlock(); } catch { /* ignore */ }
    };
  }, []);
  const [minimapData, setMinimapData] = useState<MinimapData | null>(null);
  const [zoomHandlers, setZoomHandlers] = useState<{ zoomIn: () => void; zoomOut: () => void; resetZoom: () => void; fitToScreen: () => void } | null>(null);

  const handleMinimapDataChange = useCallback((data: MinimapData) => {
    setMinimapData(data);
  }, []);

  const handleRegisterZoomHandlers = useCallback((handlers: { zoomIn: () => void; zoomOut: () => void; resetZoom: () => void; fitToScreen: () => void }) => {
    setZoomHandlers(handlers);
  }, []);

  const handleMobileAddBlock = useCallback((type: BlockType) => {
    const canvasEl = props.canvasInnerRef.current?.parentElement;
    const z = minimapData?.zoom ?? 1;
    const sl = canvasEl?.scrollLeft ?? 0;
    const st = canvasEl?.scrollTop ?? 0;
    const vw = canvasEl?.clientWidth ?? window.innerWidth;
    const vh = canvasEl?.clientHeight ?? window.innerHeight;
    const cw = minimapData?.canvasWidth ?? 4000;
    const ch = minimapData?.canvasHeight ?? 2500;
    const cx = (sl + vw / 2 - cw / 2) / z;
    const cy = (st + vh / 2 - ch / 2) / z;
    // S-027: snap till GRID_SIZE=20 (CLAUDE.md-konvention) — tidigare snap till 10
    // bröt invariant `b.x % 20 === 0` som validateAiDiagram + WorkflowState förväntar.
    const block = props.onCreateBlock(type, Math.round(cx / 20) * 20, Math.round(cy / 20) * 20);
    if (props.currentLayerGroupId) {
      props.onUpdateGroup(props.currentLayerGroupId, {
        blockIds: [...(props.groups.find(g => g.id === props.currentLayerGroupId)?.blockIds || []), block.id],
      });
    }
    toast({ title: t('blockCreated') || 'Block added', description: t(type) || type });
  }, [props, minimapData, t]);

  const handleZoomIn = useCallback(() => { zoomHandlers?.zoomIn(); }, [zoomHandlers]);
  const handleZoomOut = useCallback(() => { zoomHandlers?.zoomOut(); }, [zoomHandlers]);
  // Fit-view använder fit-to-screen istället för bara reset till 100%.
  // Det är vad user förväntar sig på mobil — visa allt content samtidigt.
  // Klampas inom tier:s [zoomMin, zoomMax] av WorkflowCanvas.
  const handleFitView = useCallback(() => { zoomHandlers?.fitToScreen(); }, [zoomHandlers]);

  const sharedProps: MobileSubLayoutProps = {
    props,
    minimapData,
    handleMinimapDataChange,
    handleRegisterZoomHandlers,
    handleMobileAddBlock,
    handleZoomIn,
    handleZoomOut,
    handleFitView,
  };

  // A-048: portrait layout (FAB buttons + bottom sheets) renders for all
  // orientations. MobileLandscapeLayout är borttagen (var dead code 327
  // rader, 0 imports utanför själva filen). User-feedback gav att FAB-
  // designen läses bättre på små skärmar oavsett rotation. Drift-lock i
  // src/test/mobileLayoutRoutingDrift.test.ts.
  return <MobilePortraitLayout {...sharedProps} />;
}
